import React from "react";
import { CARD } from "./tokens";

/* Form-layout preference: "horizontal" (one section at a time, the stepper) or "vertical"
   (sections stack up on one page as you continue). Kept in a cookie for a year so the
   merchant's choice survives sessions — one preference for every create-order desk.
   Default: horizontal. */
export const LAYOUT_COOKIE = "wv_cfo_layout";
export const readLayoutPreference = () => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${LAYOUT_COOKIE}=([^;]*)`));
    return m && decodeURIComponent(m[1]) === "vertical" ? "vertical" : "horizontal";
  } catch { return "horizontal"; }
};
export const writeLayoutPreference = (mode) => {
  try { document.cookie = `${LAYOUT_COOKIE}=${mode}; path=/; max-age=31536000; SameSite=Lax`; } catch { /* cookies blocked: the choice still applies this session */ }
};
// Tiny glyphs for the layout picker: a row of steps vs a stack of sections.
export function LayoutStepsGlyph({ active }) {
  const c = active ? "#3877FC" : "#94A3B8";
  return (
    <svg width="44" height="22" viewBox="0 0 44 22" fill="none" aria-hidden="true">
      <rect x="1" y="4" width="12" height="14" rx="3" fill={c} />
      <rect x="16" y="4" width="12" height="14" rx="3" stroke={c} strokeWidth="1.5" />
      <rect x="31" y="4" width="12" height="14" rx="3" stroke={c} strokeWidth="1.5" strokeDasharray="2.5 2" />
    </svg>
  );
}
export function LayoutStackGlyph({ active }) {
  const c = active ? "#3877FC" : "#94A3B8";
  return (
    <svg width="44" height="22" viewBox="0 0 44 22" fill="none" aria-hidden="true">
      <rect x="6" y="1" width="32" height="5" rx="2" fill={c} />
      <rect x="6" y="8.5" width="32" height="5" rx="2" stroke={c} strokeWidth="1.5" />
      <rect x="6" y="16" width="32" height="5" rx="2" stroke={c} strokeWidth="1.5" strokeDasharray="2.5 2" />
    </svg>
  );
}

/* The preference card itself — how the merchant likes to fill the form; remembered for a year. */
export function FormLayoutCard({ layoutMode, onChoose }) {
  return (
    <div className={`${CARD} relative z-10 mt-7 p-4`}>
      <div className="text-[13px] font-semibold text-slate-900">Form layout</div>
      <div className="mt-0.5 text-[12px] text-slate-500">How you like to fill the form. Saved for next time.</div>
      <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Form layout">
        {[
          { id: "horizontal", label: "Steps", hint: "One section at a time", Glyph: LayoutStepsGlyph },
          { id: "vertical", label: "Stacked", hint: "Sections build up on one page", Glyph: LayoutStackGlyph },
        ].map((o) => {
          const on = layoutMode === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChoose(o.id)}
              className={`flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${on ? "border-[#3877FC] bg-[#3877FC]/[0.05]" : "border-[#E4E9F1] bg-white hover:border-[#C4D2E8]"}`}
            >
              <o.Glyph active={on} />
              <span className={`text-[12.5px] font-semibold ${on ? "text-[#2A59BD]" : "text-slate-800"}`}>{o.label}</span>
              <span className="text-[11px] leading-snug text-slate-500">{o.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
